import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { LevelSelect } from "@/payroll/workbook-access/level-select";
import { OptionCheckbox } from "@/payroll/workbook-access/option-checkbox";
import type { AreaGrantForm } from "@/payroll/workbook-access/person-access-form";

interface AreaGrantRowProps {
  areaKey: string;
  name: string;
  sheets: string[];
  grant: AreaGrantForm;
  // With the whole workbook, an area only adds sign-off.
  approvalOnly: boolean;
  notifyUnavailableReason?: string;
  // A removed person's grants may be kept or dropped, never edited.
  locked?: boolean;
  onChange: (grant: AreaGrantForm) => void;
}

export const AreaGrantRow = ({
  areaKey,
  name,
  sheets,
  grant,
  approvalOnly,
  notifyUnavailableReason,
  locked,
  onChange,
}: AreaGrantRowProps) => {
  const id = `area-${areaKey}`;
  const tabs = sheets.length
    ? `Tabs: ${sheets.join(", ")}`
    : "No tabs in the latest workbook";
  const active = approvalOnly ? grant.approves : grant.enabled;

  return (
    <Stack
      align="start"
      gap={2}
      className={cn("w-full rounded-lg px-3 py-3", {
        "bg-selected text-brand hover:bg-selected-hovered": active,
      })}
    >
      {approvalOnly ? (
        <OptionCheckbox
          id={`${id}-approves`}
          label={`Approves ${name}`}
          hint={tabs}
          checked={grant.approves}
          disabled={locked}
          onCheckedChange={(checked) =>
            onChange({ ...grant, enabled: checked, approves: checked })
          }
        />
      ) : (
        <OptionCheckbox
          id={id}
          label={name}
          hint={tabs}
          checked={grant.enabled}
          disabled={locked}
          onCheckedChange={(checked) =>
            onChange({ ...grant, enabled: checked })
          }
        />
      )}
      {grant.enabled && !approvalOnly && (
        <Stack align="start" gap={2} className="w-full pl-6">
          <LevelSelect
            value={grant.level}
            label={`Access level for ${name}`}
            disabled={locked}
            onChange={(level) => onChange({ ...grant, level })}
          />
          <OptionCheckbox
            id={`${id}-approves`}
            label="Approves these tabs"
            checked={grant.approves}
            disabled={locked}
            onCheckedChange={(approves) => onChange({ ...grant, approves })}
          />
          <OptionCheckbox
            id={`${id}-notify`}
            label="Emailed technicians' pay questions"
            hint={notifyUnavailableReason}
            checked={grant.notify}
            disabled={locked || (!!notifyUnavailableReason && !grant.notify)}
            onCheckedChange={(notify) => onChange({ ...grant, notify })}
          />
        </Stack>
      )}
    </Stack>
  );
};
